import {
  getObject,
  getOptionalObject,
  getString,
  getOptionalString,
} from "@surya-digital/leo-ts-types";
import { SurveySettings, SurveySettingsDTO, SurveySettingsEnums } from "../types/surveySettings";
import { PrimaryColorTokens, PrimaryColorTokensDTO } from "./primaryColorTokens";
import { SurveySettingDialogProperties, SurveySettingDialogPropertiesDTO } from "../types/surveySettingDialogProperties";

export interface RespondentSurveySettingsDTO {
  surveySettings: SurveySettingsDTO;
  lightThemeTokens: PrimaryColorTokensDTO;
  darkThemeTokens: PrimaryColorTokensDTO;
  restartSurveyDialogProperties?: SurveySettingDialogPropertiesDTO | null;
  saveDraftDialogProperties?: SurveySettingDialogPropertiesDTO | null;
  surveyInformationDialogProperties?: SurveySettingDialogPropertiesDTO | null;
  surveyRespondentHeaderText?: string | null;
}

export class RespondentSurveySettings {
  readonly isRespondentSurveySettingsRespondentSurveySettings: boolean = true;

  readonly surveySettings: SurveySettings;
  readonly lightThemeTokens: PrimaryColorTokens;
  readonly darkThemeTokens: PrimaryColorTokens;
  readonly restartSurveyDialogProperties: SurveySettingDialogProperties | null;
  readonly saveDraftDialogProperties: SurveySettingDialogProperties | null;
  readonly surveyInformationDialogProperties: SurveySettingDialogProperties | null;
  readonly surveyRespondentHeaderText: string | null;


  constructor(
  surveySettings: SurveySettings, 
  lightThemeTokens: PrimaryColorTokens, 
  darkThemeTokens: PrimaryColorTokens, 
  restartSurveyDialogProperties: SurveySettingDialogProperties | null = null, 
  saveDraftDialogProperties: SurveySettingDialogProperties | null = null, 
  surveyInformationDialogProperties: SurveySettingDialogProperties | null = null, 
  surveyRespondentHeaderText: string | null = null
  ) {
    this.surveySettings = surveySettings;
    this.lightThemeTokens = lightThemeTokens;
    this.darkThemeTokens = darkThemeTokens;
    this.restartSurveyDialogProperties = restartSurveyDialogProperties;
    this.saveDraftDialogProperties = saveDraftDialogProperties;
    this.surveyInformationDialogProperties = surveyInformationDialogProperties;
    if ( surveyRespondentHeaderText !== undefined && surveyRespondentHeaderText !== null) {
      RespondentSurveySettings.validateSurveyRespondentHeaderText(surveyRespondentHeaderText);
    }
    this.surveyRespondentHeaderText = surveyRespondentHeaderText;
  }

  static fromDTO(dto: RespondentSurveySettingsDTO): RespondentSurveySettings {
    const surveySettings = SurveySettings.fromDTO(getObject(dto, "surveySettings", InvalidRespondentSurveySettingsSurveySettingsError));
    const lightThemeTokens = PrimaryColorTokens.fromDTO(getObject(dto, "lightThemeTokens", InvalidRespondentSurveySettingsLightThemeTokensError));
    const darkThemeTokens = PrimaryColorTokens.fromDTO(getObject(dto, "darkThemeTokens", InvalidRespondentSurveySettingsDarkThemeTokensError));
    let restartSurveyDialogProperties = getOptionalObject(dto, "restartSurveyDialogProperties");
    if(restartSurveyDialogProperties !== undefined && restartSurveyDialogProperties !== null) {
      restartSurveyDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "restartSurveyDialogProperties", InvalidRespondentSurveySettingsRestartSurveyDialogPropertiesError));
    }
    let saveDraftDialogProperties = getOptionalObject(dto, "saveDraftDialogProperties");
    if(saveDraftDialogProperties !== undefined && saveDraftDialogProperties !== null) {
      saveDraftDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "saveDraftDialogProperties", InvalidRespondentSurveySettingsSaveDraftDialogPropertiesError));
    }
    let surveyInformationDialogProperties = getOptionalObject(dto, "surveyInformationDialogProperties");
    if(surveyInformationDialogProperties !== undefined && surveyInformationDialogProperties !== null) {
      surveyInformationDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "surveyInformationDialogProperties", InvalidRespondentSurveySettingsSurveyInformationDialogPropertiesError));
    }
    const surveyRespondentHeaderText = getOptionalString(dto, "surveyRespondentHeaderText", InvalidRespondentSurveySettingsSurveyRespondentHeaderTextError);
    return new RespondentSurveySettings(
      surveySettings,
      lightThemeTokens,
      darkThemeTokens,
      restartSurveyDialogProperties,
      saveDraftDialogProperties,
      surveyInformationDialogProperties,
      surveyRespondentHeaderText
    );
  }

  toDTO(): RespondentSurveySettingsDTO {
    const dto: RespondentSurveySettingsDTO = {
        surveySettings: this.surveySettings.toDTO(),
        lightThemeTokens: this.lightThemeTokens.toDTO(),
        darkThemeTokens: this.darkThemeTokens.toDTO(),
    };
    if (this.restartSurveyDialogProperties !== undefined && this.restartSurveyDialogProperties !== null){
      dto.restartSurveyDialogProperties = this.restartSurveyDialogProperties.toDTO();
    }
    if (this.saveDraftDialogProperties !== undefined && this.saveDraftDialogProperties !== null){
      dto.saveDraftDialogProperties = this.saveDraftDialogProperties.toDTO();
    }
    if (this.surveyInformationDialogProperties !== undefined && this.surveyInformationDialogProperties !== null){
      dto.surveyInformationDialogProperties = this.surveyInformationDialogProperties.toDTO();
    }
    if (this.surveyRespondentHeaderText !== undefined && this.surveyRespondentHeaderText !== null){
      dto.surveyRespondentHeaderText = this.surveyRespondentHeaderText;
    }
    return dto;
  }
  private static validateSurveyRespondentHeaderText = (surveyRespondentHeaderText: string): void => {
    if(surveyRespondentHeaderText.trim().length === 0) {
      throw new InvalidRespondentSurveySettingsSurveyRespondentHeaderTextNonEmptyError(
        `Attribute surveyRespondentHeaderText cannot be an empty string.`
      );
    }
  }
  
}

RespondentSurveySettings.prototype.toString = function toString(): string {
  return `RespondentSurveySettings(surveySettings='${this.surveySettings}',lightThemeTokens='${this.lightThemeTokens}',darkThemeTokens='${this.darkThemeTokens}',restartSurveyDialogProperties='${this.restartSurveyDialogProperties}',saveDraftDialogProperties='${this.saveDraftDialogProperties}',surveyInformationDialogProperties='${this.surveyInformationDialogProperties}',surveyRespondentHeaderText='${this.surveyRespondentHeaderText}')`;
};

export class InvalidRespondentSurveySettingsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsError";
  }
}
export class InvalidRespondentSurveySettingsSurveySettingsError extends InvalidRespondentSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsSurveySettingsError";
  }
}

export class InvalidRespondentSurveySettingsLightThemeTokensError extends InvalidRespondentSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsLightThemeTokensError";
  }
}

export class InvalidRespondentSurveySettingsDarkThemeTokensError extends InvalidRespondentSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsDarkThemeTokensError";
  }
}

export class InvalidRespondentSurveySettingsRestartSurveyDialogPropertiesError extends InvalidRespondentSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsRestartSurveyDialogPropertiesError";
  }
}

export class InvalidRespondentSurveySettingsSaveDraftDialogPropertiesError extends InvalidRespondentSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsSaveDraftDialogPropertiesError";
  }
}

export class InvalidRespondentSurveySettingsSurveyInformationDialogPropertiesError extends InvalidRespondentSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsSurveyInformationDialogPropertiesError";
  }
}

export class InvalidRespondentSurveySettingsSurveyRespondentHeaderTextError extends InvalidRespondentSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsSurveyRespondentHeaderTextError";
  }
}

export class InvalidRespondentSurveySettingsSurveyRespondentHeaderTextNonEmptyError extends InvalidRespondentSurveySettingsSurveyRespondentHeaderTextError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRespondentSurveySettingsSurveyRespondentHeaderTextNonEmptyError";
  }
}
